/**
 * ReservationStatusBadge — domain-aware status badge for reservation rows.
 *
 * Queue indication is text-only on the badge label:
 *   - isQueued + HARD_RESERVATION → red label text, no extra elements.
 *   - Queue position is surfaced separately in the Kolejka column.
 *
 * No row background, no extra Q indicator. Just the badge label colour.
 */

import { Badge } from '@ooh/ui/components/badge';
import type { ReservationStatus } from '@ooh/contracts';

export interface ReservationStatusBadgeProps {
  status: ReservationStatus;
  /** When true and status is HARD_RESERVATION, renders badge label in red text */
  isQueued?: boolean;
  statusLabel: string;
}

const statusVariantMap: Record<
  ReservationStatus,
  'default' | 'outline' | 'warning' | 'success' | 'destructive' | 'info'
> = {
  SOFT_RESERVATION: 'info',
  HARD_RESERVATION: 'warning',
  SOLD: 'success',
  OPTION: 'outline',
};

export function ReservationStatusBadge({
  status,
  isQueued = false,
  statusLabel,
}: ReservationStatusBadgeProps) {
  const variant = statusVariantMap[status];
  const queuedStyle = isQueued && status === 'HARD_RESERVATION';

  return (
    <Badge
      variant={variant}
      className={queuedStyle ? 'text-destructive border-destructive/40' : undefined}
    >
      {statusLabel}
    </Badge>
  );
}
